import axios from '~/utils/axiosInstance';

export type DealExclusionsListResponse = {
  exclusions: any[];
  exclusionNotes: string | null;
};

export const getDealExclusions = async function (params: {
  projectId?: string;
  portfolioId?: string;
}): Promise<DealExclusionsListResponse> {
  const response = await axios.get('/admin/deal-exclusions', { params });
  const data = response.data;
  if (Array.isArray(data)) {
    return { exclusions: data, exclusionNotes: null };
  }
  return data as DealExclusionsListResponse;
};

/** Aggregated exclusions for a company (uses path param so OpenAPI query validation is not required). */
export const getCompanyDealExclusions = async function (
  companyId: string
): Promise<DealExclusionsListResponse> {
  const response = await axios.get(
    `/admin/companies/${companyId}/deal-exclusions`
  );
  const data = response.data;
  if (Array.isArray(data)) {
    return { exclusions: data, exclusionNotes: null };
  }
  return data as DealExclusionsListResponse;
};

/** Read-only exclusions for listing tab (logged-in developer/admin with deal access). */
export const getDealExclusionsForListing = async function (params: {
  projectId?: string;
  portfolioId?: string;
}): Promise<{ exclusions: any[] }> {
  if (params.projectId) {
    const response = await axios.get(
      `/projects/${params.projectId}/deal-exclusions`
    );
    return response.data;
  }
  if (params.portfolioId) {
    const response = await axios.get(
      `/portfolios/${params.portfolioId}/deal-exclusions`
    );
    return response.data;
  }
  return { exclusions: [] };
};

export const createDealExclusion = async function (payload: {
  companyId: string;
  projectId?: string;
  portfolioId?: string;
  reason?: string;
}) {
  const response = await axios.post('/admin/deal-exclusions', payload);
  return response.data;
};

export const removeDealExclusion = async function (id: string) {
  const response = await axios.delete(`/admin/deal-exclusions/${id}`);
  return response.data;
};

export const updateDealExclusionReason = async function (payload: {
  id: string;
  reason: string | null;
}) {
  const response = await axios.patch(`/admin/deal-exclusions/${payload.id}`, {
    reason: payload.reason
  });
  return response.data;
};

export const updateDealExclusionNotes = async function (payload: {
  projectId?: string;
  portfolioId?: string;
  exclusionNotes: string | null;
}) {
  const response = await axios.patch('/admin/deal-exclusions/notes', payload);
  return response.data;
};

export type CopyDealExclusionsResult = {
  inserted: number;
  skipped: number;
  copyNotes: boolean;
};

export const copyDealExclusions = async function (payload: {
  fromProjectId?: string;
  toProjectId?: string;
  fromPortfolioId?: string;
  toPortfolioId?: string;
  copyNotes?: boolean;
}): Promise<CopyDealExclusionsResult> {
  const response = await axios.post('/admin/deal-exclusions/copy', payload);
  return response.data;
};

export type DealCopyDropdownOption = { key: string; value: string; text: string };

/** Dropdown value is `project|<hashid>` or `portfolio|<hashid>` for copy payload routing. */
export const searchProjectsForDealExclusionCopy = async function (
  query: string,
  excludeProjectId?: string
): Promise<DealCopyDropdownOption[]> {
  const term = query.trim().replace(/[%_]/g, '');
  if (!term) return [];
  const filters: Record<string, unknown> = { nameContains: term };
  if (excludeProjectId) {
    filters.excludes = [excludeProjectId];
  }
  const response = await axios.get('/admin/projects', {
    params: {
      filters: JSON.stringify(filters),
      sortProperty: 'name',
      sortOrder: 'asc'
    }
  });
  const rows = response.data as Array<{
    id: string;
    name?: string;
    installerName?: string;
  }>;
  return rows.map((p) => {
    const title = p.name || p.id;
    const sub = p.installerName ? ` (${p.installerName})` : '';
    return {
      key: `project-${p.id}`,
      value: `project|${p.id}`,
      text: `[Project] ${title}${sub}`
    };
  });
};

export const searchPortfoliosForDealExclusionCopy = async function (
  query: string,
  excludePortfolioId?: string
): Promise<DealCopyDropdownOption[]> {
  const term = query.trim().replace(/[%_]/g, '');
  if (!term) return [];
  const response = await axios.get('/portfolios', {
    params: { nameContains: term }
  });
  const rows = response.data as Array<{ id: string; name?: string }>;
  return rows
    .filter((p) => !excludePortfolioId || p.id !== excludePortfolioId)
    .map((p) => ({
      key: `portfolio-${p.id}`,
      value: `portfolio|${p.id}`,
      text: `[Portfolio] ${p.name || p.id}`
    }));
};

/** Search both deal types for the copy-from typeahead (current project/portfolio excluded from its list). */
export const searchDealsForExclusionCopy = async function (
  query: string,
  options: { excludeProjectId?: string; excludePortfolioId?: string }
): Promise<DealCopyDropdownOption[]> {
  const term = query.trim().replace(/[%_]/g, '');
  if (!term) return [];
  const [fromProjects, fromPortfolios] = await Promise.all([
    searchProjectsForDealExclusionCopy(term, options.excludeProjectId),
    searchPortfoliosForDealExclusionCopy(term, options.excludePortfolioId)
  ]);
  return [...fromProjects, ...fromPortfolios].sort((a, b) =>
    a.text.localeCompare(b.text, undefined, { sensitivity: 'base' })
  );
};
